import React, { useState } from "react";
import {
  Box,
  Flex,
  IconButton,
  Stack,
  Link,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerContent,
  Button,
  Text,
  Image,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";

export default function Navbar() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredMenu, setHoveredMenu] = useState(null);

  const navigateAndScrollTop = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" });
    onClose();
  };

  const scrollToSection = (id) => {
    if (location.pathname !== "/about") {
      navigate("/about");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          const yOffset = -80;
          const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      }, 300);
    } else {
      const el = document.getElementById(id);
      if (el) {
        const yOffset = -80;
        const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }
    onClose();
  };

  const menuLinks = [
    { name: "Home", path: "/" },
    {
      name: "About Us",
      subMenu: [
        { name: "Overview", id: "overview" },
        { name: "Why HoriSun", id: "why" },
        { name: "Our Processes", id: "processes" },
      ],
    },
    {
      name: "Services",
      subMenu: [
        { name: "Design & Engineering", path: "/services/design" },
        { name: "Supply Chain Management", path: "/services/supply-chain" },
        { name: "EPC Management (EPCM)", path: "/services/epc-management" },
        {
          name: "MW-Scale Project Management Consultancy",
          path: "/services/consultancy",
        },
        { name: "Feasibility Study", path: "/services/feasibility-study" },
        {
          name: "Consulting & Compliance Services",
          path: "/services/advisory",
        },
      ],
    },
    { name: "Portfolio", path: "/projects" },
    { name: "Careers", path: "/careers" },
    { name: "Contact", path: "/contact" },
    {
      name: "Group Companies",
      subMenu: [
        {
          name: "HoriSun Institute",
          path: "https://horisuninstitute.in/",
          external: true,
        },
      ],
    },
  ];

  return (
    <Box
      bg="white"
      color="#305D24"
      w="100%"
      px={{ base: 6, md: 14 }}
      py={4}
      boxShadow="sm"
      position="sticky"
      top="0"
      zIndex="1000"
      userSelect="none"
    >
      <Flex h={20} alignItems="center" justifyContent="space-between">
        {/* Logo */}
        <Link
          onClick={() => navigateAndScrollTop("/")}
          _hover={{ textDecoration: "none" }}
        >
          <Image
            src={logo}
            alt="HoriSun Logo"
            h={{ base: "90px", md: "110px" }}
            objectFit="contain"
            borderRadius="md"
          />
        </Link>

        {/* Desktop Menu */}
        <Flex
          display={{ base: "none", md: "flex" }}
          gap={5}
          alignItems="center"
        >
          {menuLinks.map((link) =>
            link.subMenu ? (
              <Box
                key={link.name}
                onMouseEnter={() => setHoveredMenu(link.name)}
                onMouseLeave={() => setHoveredMenu(null)}
              >
                <Menu isOpen={hoveredMenu === link.name}>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    variant="ghost"
                    color="#305D24"
                    fontWeight="semibold"
                    fontSize="md"
                    _hover={{
                      bg: "#F6A11A",
                      color: "white",
                      transform: "scale(1.08)",
                      filter: "drop-shadow(0 2px 6px rgba(246,161,26,0.6))",
                    }}
                    _focus={{
                      boxShadow: "0 0 0 3px rgba(102,182,73,0.6)",
                      outline: "none",
                    }}
                    transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
                  >
                    {link.name}
                  </MenuButton>
                  <MenuList
                    bg="white"
                    borderRadius="xl"
                    boxShadow="2xl"
                    color="#305D24"
                    minW="220px"
                    py={2}
                  >
                    {link.subMenu.map((sub) =>
                      sub.external ? (
                        <MenuItem
                          as="a"
                          href={sub.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          key={sub.name}
                          _hover={{ bg: "#F5FAF6", color: "#F6A11A" }}
                          fontWeight="medium"
                          transition="all 0.2s"
                        >
                          {sub.name}
                        </MenuItem>
                      ) : sub.id ? (
                        <MenuItem
                          key={sub.name}
                          onClick={() => scrollToSection(sub.id)}
                          _hover={{ bg: "#F5FAF6", color: "#F6A11A" }}
                          fontWeight="medium"
                          transition="all 0.2s"
                        >
                          {sub.name}
                        </MenuItem>
                      ) : (
                        <MenuItem
                          key={sub.name}
                          onClick={() => navigateAndScrollTop(sub.path)}
                          _hover={{ bg: "#F5FAF6", color: "#F6A11A" }}
                          fontWeight="medium"
                          transition="all 0.2s"
                        >
                          {sub.name}
                        </MenuItem>
                      )
                    )}
                  </MenuList>
                </Menu>
              </Box>
            ) : (
              <Link
                key={link.name}
                onClick={() => navigateAndScrollTop(link.path)}
                px={5}
                py={3}
                rounded="md"
                fontWeight="semibold"
                fontSize="md"
                _hover={{
                  bg: "#F6A11A",
                  color: "white",
                  transform: "scale(1.08)",
                  filter: "drop-shadow(0 2px 6px rgba(246,161,26,0.6))",
                }}
                _focus={{
                  boxShadow: "0 0 0 3px rgba(102,182,73,0.6)",
                  outline: "none",
                }}
                transition="all 0.25s cubic-bezier(0.4, 0, 0.2, 1)"
              >
                {link.name}
              </Link>
            )
          )}
        </Flex>

        {/* Mobile Menu Button */}
        <IconButton
          size="lg"
          icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
          aria-label="Toggle Menu"
          display={{ md: "none" }}
          onClick={isOpen ? onClose : onOpen}
          bg="#F6A11A"
          color="white"
          _hover={{ bg: "#FFD580", color: "#305D24" }}
          shadow="md"
          _focus={{ boxShadow: "0 0 0 3px rgba(102,182,73,0.6)" }}
          transition="all 0.3s ease"
        />

        {/* Mobile Drawer */}
        <Drawer placement="right" onClose={onClose} isOpen={isOpen}>
          <DrawerContent bg="#F5FAF6" p={6}>
            <DrawerBody>
              <Stack spacing={6} mt={10}>
                {menuLinks.map((link) =>
                  link.subMenu ? (
                    <Box key={link.name}>
                      <Text
                        fontWeight="bold"
                        fontSize="lg"
                        mb={3}
                        color="#519C36"
                      >
                        {link.name}
                      </Text>
                      <Stack pl={6} mt={2} spacing={3}>
                        {link.subMenu.map((sub) =>
                          sub.external ? (
                            <Link
                              as="a"
                              href={sub.path}
                              target="_blank"
                              rel="noopener noreferrer"
                              key={sub.name}
                              py={2}
                              fontWeight="medium"
                              color="#305D24"
                              rounded="md"
                              _hover={{
                                bg: "#F6A11A",
                                color: "white",
                              }}
                              transition="all 0.2s"
                            >
                              {sub.name}
                            </Link>
                          ) : sub.id ? (
                            <Link
                              key={sub.name}
                              onClick={() => scrollToSection(sub.id)}
                              py={2}
                              fontWeight="medium"
                              color="#305D24"
                              rounded="md"
                              _hover={{
                                bg: "#F6A11A",
                                color: "white",
                              }}
                              transition="all 0.2s"
                            >
                              {sub.name}
                            </Link>
                          ) : (
                            <Link
                              key={sub.name}
                              onClick={() => navigateAndScrollTop(sub.path)}
                              py={3}
                              fontWeight="semibold"
                              color="#305D24"
                              rounded="md"
                              _hover={{ bg: "#F6A11A", color: "white" }}
                              transition="all 0.2s"
                            >
                              {sub.name}
                            </Link>
                          )
                        )}
                      </Stack>
                    </Box>
                  ) : (
                    <Link
                      key={link.name}
                      onClick={() => navigateAndScrollTop(link.path)}
                      py={3}
                      fontWeight="semibold"
                      color="#305D24"
                      rounded="md"
                      _hover={{ bg: "#F6A11A", color: "white" }}
                      transition="all 0.2s"
                    >
                      {link.name}
                    </Link>
                  )
                )}
              </Stack>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Flex>
    </Box>
  );
}
